<template>
  <div class="initiative-card pointer draggable" @click="showInitiativeModal">
    <textarea
      class="text-medium-1"
      @click.stop
      @keydown="onKeyDown"
      v-model="title"
      v-if="isNew"
      data-prevent-blur="true"
      v-autofocus
      v-no-capture
    />
    <div v-else>
      <div class="title text-medium-1" v-no-capture>{{ initiativeData.title }}</div>
      <div class="d-flex align-items-center meta-row">
        <user-short :user-id="initiativeData.ownerId" :length="22" />
        <span
          class="ml-2"
          :class="getDateBadge(initiativeData.dueDate)"
          v-if="initiativeData.dueDate"
          >{{ formatDate(initiativeData.dueDate) }}</span
        >
        <span class="placeholder badge-default ml-2" v-else>Due</span>
        <i class="ml-2" :class="statusIcon" v-if="!hideStatus"></i>
      </div>
    </div>
    <div v-if="initiativeData.keyResultId" @click.stop>
      <kr-selector
        class="ml-n2"
        :krs="krs"
        :selected="initiativeData.keyResultId"
        @change="selectKr"
      />
    </div>
  </div>
</template>

<script>
import InitiativeModal from '@/components/initiative/InitiativeModal'
import KrSelector from '@/components/KrSelector'
import UserShort from '@/components/UserShort'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { DEFAULT_IMPACT } from '@/constants/idea'
import { DEFAULT_STATUS, STATUS_OPTIONS } from '@/constants/initiative'
import { KB_KEYS, KEY_CODES } from '@/constants/keyCodes'
import { DEFAULT_SUCCESS } from '@/constants/messages'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import { formatDate } from '@/utils/formatDate'
import getDateBadge from '@/utils/getDateBadge'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import isFieldACustomField from '@/utils/initiative/IsFieldACustomField'
import showAPIError from '@/utils/showAPIError'
import showModal from '@/utils/showModal'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This component shows Initiative as Card. It mainly designed for Initiative board view
 * props:
 * - initiative
 * - krs: list of krs of team/org to which initiative belongs
 * - groupBy: groupBy Param
 * - groupName: value of attributes on which grouping has been done i.e if grouping is done on priority, possible values for groupName can be low, medium, high
 *
 * @emits
 * - save: emitted when newly created initiative is saved
 * - update: emitted when an initiative is updated
 * - remove: emitted when an initiative is removed without saving it
 *
 */
export default {
  name: 'InitiativeCard',
  props: ['initiative', 'krs', 'groupBy', 'groupName', 'initiatives', 'hideStatus'],
  components: {
    UserShort,
    KrSelector
  },
  mixins: [EventBusMixin],
  data() {
    return {
      initiativeData: this.initiative,
      title: '',
      submitted: false,
      statusOptions: STATUS_OPTIONS
    }
  },
  computed: {
    kr() {
      return this.$store.getters.keyResultById(this.initiativeData.keyResultId)
    },
    isNew() {
      const initiativeId = String(this.initiativeData.id)
      return initiativeId.startsWith('local')
    },
    krTitle() {
      const kr = this.$store.getters.keyResultById(this.initiativeData.keyResultId)
      return kr ? kr.title : ''
    },
    statusIcon() {
      return this.statusOptions.find(R.propEq('id', this.initiativeData.status))?.icon
    }
  },
  methods: {
    ...mapActions(['patchInitiative', 'createInitiative']),
    getDateBadge,
    formatDate,
    async addInitiative() {
      const payload = {
        orgId: this.initiativeData.orgId,
        goalId: this.initiativeData.goalId,
        krId: this.initiativeData.keyResultId,
        projectId: this.initiativeData.projectId,
        title: this.title,
        ownerId: this.kr?.ownerId,
        coOwnerIds: [],
        impact: DEFAULT_IMPACT,
        status: DEFAULT_STATUS
      }
      if (!isFieldACustomField(this.groupBy)) {
        payload[this.groupBy] = this.groupName
      }
      try {
        const initiative = await this.createInitiative(payload)
        if (isFieldACustomField(this.groupBy)) {
          const orgId = this.initiativeData.organizationId
          const fieldId = getIdFromCFPrefixedField(this.groupBy)
          const fields = this.$store.getters.customFieldDefinitions(orgId)
          const field = fields.find(field => field.id === fieldId)
          await this.$store.dispatch('setInitiativeCustomField', {
            orgId: orgId,
            initiativeId: initiative.id,
            field: field,
            value: this.groupName
          })
        }
        this.$emit('save', initiative)
        EventBus.$emit('ADD_TOAST', { message: 'Initiative created successfully' })
      } catch (e) {
        showAPIError(e)
      }
    },
    onKeyDown(e) {
      if (e.key === KB_KEYS.ENTER || e.keyCode === KEY_CODES.ENTER) {
        e.preventDefault()
        this.submitted = true
        if (this.title && (this.initiativeData.keyResultId || this.initiativeData.projectId)) {
          this.addInitiative()
        }
      } else if (e.keyCode === KEY_CODES.ESCAPE && !this.title) {
        this.$emit('remove')
      }
    },
    async selectKr(kr) {
      try {
        const initiative = await this.patchInitiative({
          orgId: this.initiativeData.organizationId,
          initiativeId: this.initiativeData.id,
          data: {
            keyResultId: kr.id,
            goalId: kr.goalId
          }
        })
        this.$emit('update', initiative)
        EventBus.$emit('ADD_TOAST', { message: DEFAULT_SUCCESS })
      } catch (e) {
        showAPIError(e)
      }
    },
    showInitiativeModal() {
      showModal({
        component: InitiativeModal,
        id: `${ENTITY_TYPE.INITIATIVE}-${this.initiative.id}`,
        props: {
          show: true,
          initiatives: this.initiatives,
          initiative: this.initiative
        }
      })
    },
    onInitiativeUpdate(initiative) {
      if (initiative.id === this.initiative.id) {
        this.$emit('update-initiative', initiative)
      }
    }
  },
  mounted() {
    this.listenToEventBus(events.INITIATIVE_UPDATED, this.onInitiativeUpdate)
  },
  watch: {
    initiative(initiative) {
      this.initiativeData = initiative
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/components/_badge.scss';
@import '~@/styles/_styleConfigurations.scss';

.initiative-card {
  padding: 14px;
  background-color: $documentColor;
  border-radius: 10px;
  max-width: 300px;
  min-width: 200px;

  &:not(:first-child) {
    margin-top: 12px;
  }
}

.title {
  overflow-wrap: break-word;
  hyphens: auto;
}

.meta-row {
  padding: 12px 0 8px 0;
}

textarea {
  border: none;
  border: 1px solid transparent;
  background-color: $quinary;
  border-radius: 5px;
  margin-bottom: 14px;
  width: 100%;

  &:focus {
    outline: none;
    box-shadow: inset 0px 2px 1px rgba(0, 0, 0, 0.25);
  }
}
</style>
